import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Icon } from "@/components/ui/icon";
import { toast } from "sonner";
import { ExperienceForm } from "./ExperienceForm";
import { DraggableList } from "./DraggableList";
import { BulkActionBar } from "./BulkActionBar";

export const ExperienceManager = () => {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { data: experiences } = useQuery({
    queryKey: ["admin-experiences"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("work_experience")
        .select("*")
        .order("display_order");
      if (error) throw error;
      return data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("work_experience")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-experiences"] });
      queryClient.invalidateQueries({ queryKey: ["experiences"] });
      toast.success("Experience deleted successfully");
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase
        .from("work_experience")
        .delete()
        .in("id", ids);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-experiences"] });
      queryClient.invalidateQueries({ queryKey: ["experiences"] });
      setSelectedIds(new Set());
      toast.success("Selected experiences deleted");
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (items: typeof experiences) => {
      if (!items) return;
      const updates = items.map((item, index) => ({
        id: item.id,
        display_order: index,
        company_name: item.company_name,
        position: item.position,
        start_date: item.start_date,
      }));
      
      for (const update of updates) {
        const { error } = await supabase
          .from("work_experience")
          .update({ display_order: update.display_order })
          .eq("id", update.id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-experiences"] });
      queryClient.invalidateQueries({ queryKey: ["experiences"] });
      toast.success("Order updated");
    },
  });

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleReorder = useCallback((newItems: NonNullable<typeof experiences>) => {
    reorderMutation.mutate(newItems);
  }, [reorderMutation]);

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-card border-border shadow-sm rounded-xl">
        <h2 className="text-2xl font-bold text-foreground mb-6">Add New Experience</h2>
        <ExperienceForm />
      </Card>

      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-foreground">Existing Experiences</h3>
        
        {experiences && experiences.length > 0 && (
          <BulkActionBar
            selectedCount={selectedIds.size}
            totalCount={experiences.length}
            onSelectAll={() => setSelectedIds(new Set(experiences.map((e) => e.id)))}
            onDeselectAll={() => setSelectedIds(new Set())}
            onDeleteSelected={() => bulkDeleteMutation.mutate(Array.from(selectedIds))}
            isDeleting={bulkDeleteMutation.isPending}
          />
        )}

        {experiences && (
          <DraggableList
            items={experiences}
            onReorder={handleReorder}
            disabled={editingId !== null}
            renderItem={(exp) => (
              <Card className="p-6 bg-card border-border shadow-sm rounded-xl">
                {editingId === exp.id ? (
                  <div className="space-y-4">
                    <ExperienceForm
                      initialData={exp}
                      onSuccess={() => setEditingId(null)}
                    />
                    <Button
                      variant="outline"
                      onClick={() => setEditingId(null)}
                      className="w-full"
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-start gap-4">
                    <Checkbox
                      checked={selectedIds.has(exp.id)}
                      onCheckedChange={() => toggleSelection(exp.id)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-foreground">{exp.company_name}</h4>
                      <p className="text-primary">{exp.position}</p>
                      <p className="text-sm text-muted-foreground">
                        {exp.start_date} - {exp.end_date || "Present"}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setEditingId(exp.id)}
                      >
                        <Icon name="edit" size={16} />
                      </Button>
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => deleteMutation.mutate(exp.id)}
                      >
                        <Icon name="delete" size={16} />
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            )}
          />
        )}
      </div>
    </div>
  );
};
